"use client";

import {
  BookOpen,
  Building2,
  Check,
  ClipboardCheck,
  Files,
  GraduationCap,
  Layers3,
  ListChecks,
  MessageCircle,
  ScanEye,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { useId, useRef, useState, type KeyboardEvent } from "react";

import styles from "./institutional-landing-showcase.module.css";

const roles = [
  { key: "coordination", icon: UsersRound, items: [UsersRound, BookOpen, Layers3] },
  { key: "teacher", icon: GraduationCap, items: [Files, ClipboardCheck, ScanEye] },
  { key: "student", icon: BookOpen, items: [BookOpen, ListChecks, MessageCircle] },
] as const;

export function LandingShowcase() {
  const t = useTranslations("InstitutionalLanding.showcase");
  const id = useId();
  const [activeIndex, setActiveIndex] = useState(0);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);

  function moveFocus(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex: number;
    switch (event.key) {
      case "ArrowRight":
        nextIndex = (index + 1) % roles.length;
        break;
      case "ArrowLeft":
        nextIndex = (index + roles.length - 1) % roles.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = roles.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    tabs.current[nextIndex]?.focus();
  }

  return (
    <div className={styles.showcase}>
      <div className={styles.windowHeader}>
        <div className={styles.context}>
          <span className={styles.institutionIcon}><Building2 aria-hidden="true" size={19} /></span>
          <div><p>{t("institution")}</p><span>{t("subject")}</span></div>
        </div>
        <span className={styles.preview}><span aria-hidden="true" />{t("preview")}</span>
      </div>

      <div role="tablist" aria-label={t("label")} className={styles.tabs}>
        {roles.map(({ key, icon: Icon }, index) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`${id}-tab-${key}`}
            aria-selected={activeIndex === index}
            aria-controls={`${id}-panel-${key}`}
            tabIndex={focusedIndex === index ? 0 : -1}
            ref={element => { tabs.current[index] = element; }}
            onFocus={() => setFocusedIndex(index)}
            onKeyDown={event => moveFocus(event, index)}
            onClick={() => setActiveIndex(index)}
            className={styles.tab}
          >
            <Icon aria-hidden="true" size={17} />
            <span>{t(`${key}.label`)}</span>
          </button>
        ))}
      </div>

      {roles.map(({ key, items }, index) => (
        <div
          key={key}
          id={`${id}-panel-${key}`}
          role="tabpanel"
          aria-labelledby={`${id}-tab-${key}`}
          hidden={activeIndex !== index}
          tabIndex={0}
          className={styles.panel}
        >
          <div className={styles.prompt}>
            <span className={styles.eyebrow}><MessageCircle aria-hidden="true" size={14} />{t("promptLabel")}</span>
            <p>{t(`${key}.prompt`)}</p>
          </div>

          <div className={styles.response}>
            <div className={styles.responseHeading}>
              <span className={styles.assistantIcon}><Sparkles aria-hidden="true" size={18} /></span>
              <div><span>{t("assistant")}</span><p>{t("responseLabel")}</p></div>
            </div>
            <ul className={styles.items}>
              {items.map((Icon, itemIndex) => (
                <li key={itemIndex}>
                  <Icon aria-hidden="true" size={17} />
                  <span>{t(`${key}.item${itemIndex + 1}`)}</span>
                  <Check aria-hidden="true" size={14} className={styles.itemCheck} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}

      <div className={styles.review}><ScanEye aria-hidden="true" size={16} /><p>{t("review")}</p></div>
    </div>
  );
}
